import {
  Beaker,
  BookOpenCheck,
  Cable,
  CircuitBoard,
  Code2,
  Database,
  Droplets,
  FlaskConical,
  HeartPulse,
  Layers3,
  Microscope,
  Pipette,
  Search,
  Settings2,
  Wrench,
} from "lucide-react";
import scientificAiSolutions from "./scientificAiSolutions";

export const categories = [
  {
    id: "workflow",
    label: "Assays & methods",
    shortLabel: "Assays",
    icon: FlaskConical,
    description:
      "Bring a working SOP, develop an assay first, or rescue a method that keeps failing.",
  },
  {
    id: "operations",
    label: "Workcells & instruments",
    shortLabel: "Workcells",
    icon: Cable,
    description:
      "Build from a manual SOP or recover equipment that never made it into routine use.",
  },
  {
    id: "software",
    label: "Scientific software",
    shortLabel: "Scientific software",
    icon: Code2,
    description:
      "Fix notebooks, scripts, and agents that became too fragile for the lab to trust.",
  },
  {
    id: "data",
    label: "Data & scientific AI",
    shortLabel: "Data + AI",
    icon: Database,
    description:
      "Create traceable datasets, evaluations, and supervised agent workflows.",
  },
];

export const solutions = [
  {
    id: "workflow-assessment",
    category: "workflow",
    mode: "Focused sprint",
    availability: "Available",
    icon: Search,
    title: "Automation readiness & roadmap",
    summary:
      "Choose the right first workflow before committing budget, hardware, or engineering time.",
    bestFor:
      "Teams with several automation candidates and no agreed place to start.",
    deliverables: [
      "Workflow and constraint map",
      "Prioritized automation opportunities",
      "Recommended scope, risks, and acceptance criteria",
    ],
    cta: "Assess my workflow",
  },
  {
    id: "assay-automation",
    category: "workflow",
    mode: "Scoped delivery",
    availability: "Available",
    icon: FlaskConical,
    title: "Onboard and automate a working assay",
    summary:
      "Turn one working assay into a reviewed, executable workflow with named inputs and optional natural-language access.",
    bestFor:
      "Assay owners with a repeatable method that has become a throughput or consistency bottleneck.",
    deliverables: [
      "Editable method files in the agreed platform format",
      "Deck layout, labware definitions, and operating settings",
      "Named run inputs and an approved command interface",
      "Test record against assay-owner criteria",
      "Known limits, recovery notes, and operator runbook",
    ],
    cta: "Bring us one SOP",
    featured: true,
  },
  {
    id: "cell-based-assay-automation",
    category: "workflow",
    mode: "Scoped delivery",
    availability: "Available",
    icon: Microscope,
    title: "Automate a cell-based assay",
    summary:
      "Coordinate plate setup, reagent addition, timed handling, imaging, and readouts around the needs of the cells.",
    bestFor:
      "Cell biology teams whose manual plate workflow is limiting consistency, throughput, or operating hours.",
    deliverables: [
      "Workflow map with biological timing and handling constraints",
      "Instrument method, plate logistics, and operator handoffs",
      "Test record, recovery plan, and runbook",
    ],
    cta: "Review a cell workflow",
    featured: true,
  },
  {
    id: "ipsc-differentiation",
    category: "workflow",
    mode: "Program build",
    availability: "Scoped pilot",
    icon: HeartPulse,
    title: "Coordinate an iPSC differentiation workflow",
    summary:
      "Build a staged workflow for media schedules, plate handling, microscopy checkpoints, and QC gates across a long-running differentiation.",
    bestFor:
      "Cell therapy and disease-model teams scaling a differentiation with clear stage-specific acceptance criteria.",
    deliverables: [
      "Stage map, schedule, inputs, and QC decision points",
      "Automated or assisted methods for the agreed steps",
      "Batch record, exceptions, and operator runbook",
    ],
    cta: "Map a differentiation",
  },
  {
    id: "sirna-screening",
    category: "workflow",
    mode: "Screening workflow",
    availability: "Scoped pilot",
    icon: Search,
    title: "Build a repeatable siRNA screen",
    summary:
      "Connect library handling, plate maps, perturbation, controls, readouts, and QC into a screen your team can rerun.",
    bestFor:
      "Discovery teams moving from small perturbation experiments to multi-plate screening.",
    deliverables: [
      "Plate and control design with sample traceability",
      "Automation methods and run sequence",
      "QC checks, normalized output, and hit-review handoff",
    ],
    cta: "Scope an siRNA screen",
  },
  {
    id: "antibody-screening",
    category: "workflow",
    mode: "Screening workflow",
    availability: "Available",
    icon: Beaker,
    title: "Automate an antibody screen",
    summary:
      "Run traceable dilution series, binding assays, wash steps, and readouts from candidate plate to ranked result.",
    bestFor:
      "Antibody discovery teams repeating ELISA-style or related plate workflows across growing candidate sets.",
    deliverables: [
      "Candidate, dilution, plate, and control design",
      "Instrument methods with wash and readout handling",
      "Run record, QC summary, and ranked output",
    ],
    cta: "Review an antibody screen",
  },
  {
    id: "method-development",
    category: "workflow",
    mode: "Scoped or embedded",
    availability: "Available",
    icon: Pipette,
    title: "Get a liquid-handler method over the line",
    summary:
      "We build, debug, and document one method on the exact instrument and software version agreed in scope.",
    bestFor:
      "Automation teams that know what to run but need more method-development capacity.",
    deliverables: [
      "Method files in the agreed platform format and deck layout",
      "Labware, transfer, timing, and error-handling settings",
      "Test record, known limits, and operator runbook",
    ],
    cta: "Get help with a method",
    featured: true,
  },
  {
    id: "bespoke-assay-development",
    category: "workflow",
    mode: "Scoped delivery",
    availability: "Available",
    icon: Beaker,
    title: "Develop a plate assay for automation",
    summary:
      "Develop or optimize a plate-based assay with controls, liquid handling, and eventual automation transfer considered from the start.",
    bestFor:
      "Biology teams establishing a new plate-based assay or readout.",
    deliverables: [
      "Experimental plan and critical parameters",
      "Method iterations with agreed decision points",
      "Transfer-ready SOP and automation requirements",
    ],
    boundary:
      "Assay development is scoped as an off-site R&D project. Work at your site covers automation installation, integration, and agreed acceptance testing—not open-ended assay development.",
    cta: "Scope assay development",
  },
  {
    id: "liquid-class-development",
    category: "workflow",
    mode: "Focused study",
    availability: "Available",
    icon: Droplets,
    title: "Tune transfers that keep missing",
    summary:
      "We test the fluid and transfer conditions behind bubbles, hanging drops, foam, carryover, or poor recovery.",
    bestFor:
      "Teams seeing bubbles, residual volume, carryover, or inconsistent transfers.",
    deliverables: [
      "Transfer matrix covering volumes, labware, tips, and relevant conditions",
      "Candidate settings with test results",
      "Recommended operating range and verification steps",
    ],
    cta: "Show us the troublesome transfer",
  },
  {
    id: "method-transfer",
    category: "workflow",
    mode: "Scoped delivery",
    availability: "Available",
    icon: Layers3,
    title: "Method transfer & migration",
    summary:
      "Port an existing method to a new instrument, vendor, site, or operating model.",
    bestFor:
      "Teams replacing hardware, opening a site, or inheriting a method they did not build.",
    deliverables: [
      "Source-to-target gap assessment",
      "Ported method tested on the agreed platform",
      "Comparison report, runbook, and ownership handoff",
    ],
    cta: "Assess a method transfer",
  },
  {
    id: "automation-rescue",
    category: "workflow",
    mode: "Diagnostic sprint",
    availability: "Available",
    icon: Wrench,
    title: "Automation rescue & optimization",
    summary:
      "Diagnose a fragile method, stalled deployment, or workflow that only one person can run.",
    bestFor:
      "Labs with existing automation that is unreliable, slow, or difficult to maintain.",
    deliverables: [
      "Root-cause and failure-mode review",
      "Prioritized fixes and method changes",
      "Recovery plan, runbook, and ownership handoff",
    ],
    cta: "Diagnose my workflow",
  },
  {
    id: "workcell-design-install",
    category: "operations",
    mode: "Design + deployment",
    availability: "Available",
    icon: CircuitBoard,
    title: "Design and install an on-prem workcell",
    summary:
      "Start from a manual SOP or recover equipment that never became a working system. We help choose what fits, integrate it, install it, and get the first workflow running.",
    bestFor:
      "Labs starting with no automation, or labs with expensive devices that are installed but barely used.",
    deliverables: [
      "Workflow, throughput, site, and architecture definition",
      "Device-fit and purchasing recommendations, including what existing hardware to keep",
      "Instrument selection, layout, interfaces, and safety handoffs",
      "Integrated workflow with factory and site acceptance-test evidence",
      "On-prem installation, operator training, runbook, and ownership handoff",
    ],
    cta: "Design my workcell",
    featured: true,
  },
  {
    id: "workcell-integration",
    category: "operations",
    mode: "Integration project",
    availability: "Available",
    icon: Cable,
    title: "Make the workcell behave like one system",
    summary:
      "Connect the agreed instruments and software around one workflow, including starts, handoffs, status signals, errors, retries, and recovery.",
    bestFor:
      "Platform teams moving from standalone instruments to coordinated execution.",
    deliverables: [
      "Interface map for the exact devices and software in scope",
      "Sequence, state, and operator-handoff model",
      "Test record for selected alarms, pauses, retries, and manual recovery cases",
      "Log locations, restart procedure, deployment notes, and support ownership after handoff",
    ],
    cta: "Walk us through the workcell",
    featured: true,
  },
  {
    id: "governed-orchestration",
    category: "operations",
    mode: "Design partnership",
    availability: "Co-design only",
    icon: Settings2,
    title: "Orchestrate one lab workflow",
    summary:
      "Design how one workflow moves between instruments and people, including who can start, pause, retry, and stop it.",
    bestFor:
      "Automation teams defining permissions, preflight checks, safe stops, retries, and recovery across a workcell.",
    deliverables: [
      "Workflow state and ownership model",
      "Approval, exception, and recovery design",
      "Pilot architecture with agreed pass criteria",
    ],
    cta: "Design an orchestration pilot",
  },
  {
    id: "cro-data-harmonization",
    category: "data",
    mode: "Scoped delivery",
    availability: "Scoped pilot",
    icon: Database,
    title: "Stop rebuilding the same CRO report every month",
    summary:
      "CRO files arrive with different names, units, layouts, and revision histories. We map them into one traceable dataset and give your team a reporting workflow it can run again.",
    bestFor:
      "Teams with recurring CRO deliveries and too much scientist time spent reconciling them.",
    deliverables: [
      "Every original delivery preserved and inventoried",
      "Agreed mappings for sample IDs, units, endpoints, and versions",
      "A review queue for missing, conflicting, or changed records",
      "Versioned analysis tables and a repeatable reporting runbook",
      "Aggregated outputs prepared for sponsor review and approval",
    ],
    cta: "Review one CRO delivery",
    featured: true,
  },
  {
    id: "scientific-dataset-engineering",
    category: "data",
    mode: "Scoped delivery",
    availability: "Scoped pilot",
    icon: BookOpenCheck,
    title: "Turn scattered experiment files into a dataset your team can keep using",
    summary:
      "We trace each field to its source, automate the repeatable transformations, and send ambiguous records to the scientist who can resolve them.",
    bestFor:
      "Teams preparing recurring analysis, a migration, or an AI-assisted workflow.",
    deliverables: [
      "Source-to-target schema, provenance, and lineage",
      "Reproducible ingestion and transformation pipeline",
      "Validation tests, exception handling, and documented release",
    ],
    cta: "Scope a dataset build",
  },
  ...scientificAiSolutions,
];
